import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileDown, Filter, ShieldCheck, Loader2, ImageIcon, X, ChevronRight } from 'lucide-react';
import { fetchInspeccionesPeriodo, fetchDiasSinOperacion, signedUrls, fetchNombres } from 'services/adminService';
import { flattenHallazgos, filtrarHallazgos } from 'lib/hallazgos';
import {
  vigentes, coberturaPorDia, resumenCobertura, textoResumenPeriodo, fechaCorta, fechaHoraCorta, plural,
} from 'lib/auditoria';
import { checklistDe, tituloInstalacion } from 'lib/auditoriaInstalaciones';
import { hoyCR } from 'lib/fechaCR';
import { ESTADO_FILA } from 'lib/estadoUi';
import { rutaInspeccion } from 'lib/auditoriaInspeccion';
import { useAuth } from 'contexts/AuthContext';
import AppShell from 'components/AppShell';
import SelectorInstalacion, { useInstalacion } from 'components/SelectorInstalacion';
import Cargando from 'components/Cargando';
import EstadoPill, { MarcaTardio } from 'components/EstadoPill';
import PeriodoSelector, { periodoInicial, ATAJOS_HISTORIAL } from 'components/PeriodoSelector';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';
import ErrorState from 'components/ui/ErrorState';
import EmptyState from 'components/ui/EmptyState';
import { mensajeError } from 'lib/errores';

// `hallazgosPdf` arrastra `jspdf` (~300KB): se carga solo al exportar.
const loadPdfModule = () => import('lib/hallazgosPdf');

const POR_PAGINA = 20;
const clave = (h) => `${h.inspeccion_id}:${h.itemKey}`;

// Resumen del período para pantalla y PDF. completo = false si no se pudo
// consultar los días sin operación: entonces NO se cuentan días sin inspección
// (bug N2: el PDF los contaba como "sin inspección" sin avisar).
export function resumenHallazgos({ rows, sinOp, periodo, hoy }) {
  const completo = Array.isArray(sinOp);
  const c = resumenCobertura(coberturaPorDia({
    inspecciones: rows, diasSinOperacion: sinOp || [], desde: periodo.desde, hasta: periodo.hasta, hoy,
  }));
  return {
    inspecciones: vigentes(rows).length,
    sinRegistro: completo ? c.sinRegistro : 0,
    sinOperacion: completo ? c.sinOperacion : 0,
    completo,
  };
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
export function periodoDeUrl(params, hoy) {
  const desde = params.get('desde');
  const hasta = params.get('hasta');
  if (!ISO.test(desde || '') || !ISO.test(hasta || '') || desde > hasta || desde > hoy) return null;
  return { atajo: 'custom', desde, hasta: hasta > hoy ? hoy : hasta };
}

export default function Hallazgos() {
  const { user } = useAuth();
  const hoy = hoyCR();
  const [instalacion, setInstalacion] = useInstalacion();
  const [params] = useSearchParams();
  // El reporte enlaza aquí con su período (?desde=...&hasta=...).
  const [periodo, setPeriodo] = useState(() => periodoDeUrl(params, hoy) || periodoInicial('30'));
  const [filtros, setFiltros] = useState({ estado: '', item: '' });
  const [rows, setRows] = useState([]);
  const [sinOp, setSinOp] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [visibles, setVisibles] = useState(POR_PAGINA);
  const [urls, setUrls] = useState({}); // ruta -> url firmada | null
  const [exportando, setExportando] = useState(false);
  const [errorExport, setErrorExport] = useState('');

  const items = checklistDe(instalacion)?.items || [];

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [r, s] = await Promise.all([
        fetchInspeccionesPeriodo({ ...periodo, instalacion }),
        fetchDiasSinOperacion({ ...periodo, instalacion }).catch(() => null),
      ]);
      setRows(r);
      setSinOp(s);
      setVisibles(POR_PAGINA);
    } catch (e) {
      setError(e);
    } finally {
      setCargando(false);
    }
  }, [periodo, instalacion]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setFiltros((f) => ({ ...f, item: '' })); }, [instalacion]);

  const todos = useMemo(() => flattenHallazgos(rows), [rows]);
  const hallazgos = useMemo(() => filtrarHallazgos(todos, filtros), [todos, filtros]);
  const lista = hallazgos.slice(0, visibles);
  const resumen = useMemo(() => resumenHallazgos({ rows, sinOp, periodo, hoy }), [rows, sinOp, periodo, hoy]);

  // Miniaturas: se firman en lote solo las de la página visible, sin bloquear la lista.
  useEffect(() => {
    const faltan = [...new Set(lista.flatMap((i) => i.fotos || []))].filter((p) => !(p in urls));
    if (!faltan.length) return undefined;
    let vivo = true;
    signedUrls(faltan).then((m) => { if (vivo) setUrls((u) => ({ ...u, ...m })); });
    return () => { vivo = false; };
  }, [lista, urls]);

  const hayFiltros = !!(filtros.estado || filtros.item);
  const textoFiltros = [
    filtros.estado ? `estado: ${filtros.estado}` : '',
    filtros.item ? `ítem: ${items.find((i) => i.key === filtros.item)?.label || filtros.item}` : '',
  ].filter(Boolean).join(', ');

  const exportar = async () => {
    setExportando(true);
    setErrorExport('');
    try {
      const { exportHallazgosPdf } = await loadPdfModule();
      await exportHallazgosPdf(hallazgos, {
        rango: `Período ${periodo.desde} a ${periodo.hasta}`,
        filtros: textoFiltros,
        resumen,
        instalacion,
        generadoPor: (user?.id && (await fetchNombres([user.id]))[user.id]) || user?.email,
        archivo: `${periodo.desde}_a_${periodo.hasta}`,
      });
    } catch (e) {
      setErrorExport(mensajeError(e, { contexto: 'No se pudo generar el PDF.', porDefecto: 'Intente de nuevo.' }));
    } finally {
      setExportando(false);
    }
  };

  return (
    <AppShell titulo="Hallazgos de inspección" descripcion={`Ítems en alerta o falla · ${tituloInstalacion(instalacion)}`}
      acciones={(
        <Button variant="outline" onClick={exportar} disabled={cargando || !!error} loading={exportando}>
          {!exportando && <FileDown size={16} className="mr-1.5" aria-hidden="true" />}
          {exportando ? 'Generando…' : 'Descargar PDF'}
        </Button>
      )}>
      <div className="space-y-4">
        <SelectorInstalacion value={instalacion} onChange={setInstalacion} />
        <PeriodoSelector value={periodo} onChange={setPeriodo} atajos={ATAJOS_HISTORIAL} />

        <div className="rounded-xl border border-border bg-card p-3">
          <div className="mb-2.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <Filter size={13} aria-hidden="true" /> Filtros
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              Estado
              <Select value={filtros.estado} size="sm" aria-label="Estado"
                onChange={(v) => { setFiltros((f) => ({ ...f, estado: v })); setVisibles(POR_PAGINA); }}>
                <option value="">Todos</option>
                <option value="falla">Falla</option>
                <option value="alerta">Alerta</option>
              </Select>
            </label>
            <label className="flex min-w-[180px] flex-1 flex-col gap-1 text-xs font-medium text-muted-foreground">
              Ítem
              <Select value={filtros.item} size="sm" aria-label="Ítem"
                onChange={(v) => { setFiltros((f) => ({ ...f, item: v })); setVisibles(POR_PAGINA); }}>
                <option value="">Todos los ítems</option>
                {items.map((it) => <option key={it.key} value={it.key}>{it.label}</option>)}
              </Select>
            </label>
            {hayFiltros && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setFiltros({ estado: '', item: '' })}>
                <X size={14} className="mr-1" aria-hidden="true" /> Limpiar
              </Button>
            )}
          </div>
        </div>

        {errorExport && <p role="alert" className="rounded-lg bg-error/10 px-3 py-2 text-sm text-error">{errorExport}</p>}

        {error ? (
          <ErrorState title="No se pudieron cargar los hallazgos" message={mensajeError(error)} onRetry={cargar} />
        ) : cargando ? (
          <Cargando texto="Cargando hallazgos…" />
        ) : (
          <>
            <p className="text-sm text-muted-foreground" data-testid="resumen-periodo">
              {textoResumenPeriodo(resumen)}
              {' '}{plural(hallazgos.length, 'hallazgo', 'hallazgos')} en alerta o falla{hayFiltros ? ' con los filtros' : ''}.
            </p>
            {hallazgos.length === 0 ? (
              <EmptyState framed icon={<ShieldCheck size={24} />} title="Sin alertas ni fallas"
                description={hayFiltros ? 'Ningún hallazgo coincide con los filtros.' : 'No se registraron ítems en alerta o falla en este período.'} />
            ) : (
              <div className="space-y-3">
                {lista.map((h) => (
                  <article key={clave(h)} data-testid="hallazgo"
                    className={`rounded-xl border border-border bg-card p-4 ${ESTADO_FILA[h.estado] || ''}`}>
                    <div className="flex flex-wrap items-center gap-2.5">
                      <EstadoPill estado={h.estado} />
                      <h3 className="min-w-0 flex-1 text-[15px] font-bold leading-tight text-foreground">{h.item}</h3>
                      {h.tardio && <MarcaTardio />}
                    </div>
                    <p className="mt-1.5 text-xs font-medium text-muted-foreground">
                      {fechaCorta(h.fecha) || 'Sin fecha'}
                      <span className="px-1.5 text-muted-foreground/50">·</span>
                      {h.guia || 'Sin nombre'}
                      {h.created_at && (<><span className="px-1.5 text-muted-foreground/50">·</span>registrada {fechaHoraCorta(h.created_at)}</>)}
                    </p>
                    <p className="mt-2.5 whitespace-pre-line text-sm leading-snug text-foreground">
                      {h.nota || <span className="italic text-muted-foreground">Sin nota registrada.</span>}
                    </p>
                    {h.fotos.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {h.fotos.map((p, i) => (urls[p]
                          ? (
                            <a key={p} href={urls[p]} target="_blank" rel="noreferrer">
                              <img src={urls[p]} alt={`Evidencia ${h.item} ${i + 1}`} loading="lazy"
                                className="h-20 w-20 rounded-xl object-cover ring-1 ring-black/5" />
                            </a>
                          ) : (
                            <div key={p} className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl bg-muted text-center text-[10px] text-muted-foreground">
                              {p in urls ? <><ImageIcon size={14} aria-hidden="true" />No disponible</> : <Loader2 size={14} className="animate-spin" />}
                            </div>
                          )))}
                      </div>
                    )}
                    <Link to={rutaInspeccion(h.inspeccion_id)}
                      className="mt-3 inline-flex items-center gap-0.5 text-sm font-medium text-primary underline-offset-2 hover:underline">
                      Ver inspección <ChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </article>
                ))}
                <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
                  <span>Mostrando {lista.length} de {hallazgos.length}</span>
                  {lista.length < hallazgos.length && (
                    <Button type="button" variant="outline" onClick={() => setVisibles((v) => v + POR_PAGINA)}>
                      Mostrar {Math.min(POR_PAGINA, hallazgos.length - lista.length)} más
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
